import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import MainButton from '../../../shared/layout/buttons/MainButton';
import MetricTrustBadge from './components/MetricTrustBadge';
import { fetchAdminReconciliation } from '../../../services/Http/AdminApi';
import { useCurrency } from '../../../context/CurrencyProvider';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminReconciliation = () => {
    const { formatEurAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await fetchAdminReconciliation({ period });
            setData(resp?.data || null);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load reconciliation');
        } finally {
            setLoading(false);
        }
    }, [period]);
    useEffect(() => { load(); }, [load]);

    const mismatchColumns = useMemo(() => ([
        { key: 'key', label: 'Check' },
        { key: 'computed', label: 'Computed', render: (r) => formatEurAmount(r.computed) },
        { key: 'billed', label: 'Billed', render: (r) => formatEurAmount(r.billed) },
        { key: 'difference', label: 'Difference', render: (r) => <Diff $negative={Number(r.difference) < 0}>{formatEurAmount(r.difference)}</Diff> },
    ]), [formatEurAmount]);

    return (
        <PageContainer>
            <PageHeader><PageTitle>Revenue Reconciliation</PageTitle></PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <LeftRow>
                        <PeriodSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </PeriodSelect>
                        <MetricTrustBadge tier="derived" />
                    </LeftRow>
                    <MainButton size="small" onClick={load}>Refresh</MainButton>
                </AdminFilterBar>

                <StatsGrid>
                    <StatCard icon="ƒ" title="Tx Fee Computed" value={formatEurAmount(data?.summary?.transaction_fee_computed)} color="info" />
                    <StatCard icon="×" title="Cross-sell Computed" value={formatEurAmount(data?.summary?.cross_sell_computed)} color="success" />
                    <StatCard icon="B" title="Bills Total" value={formatEurAmount(data?.summary?.bills_total)} color="warning" />
                    <StatCard icon="I" title="Invoices Total" value={formatEurAmount(data?.summary?.invoices_total)} color="transactions" />
                    <StatCard icon="U" title="Unpaid Bills" value={Number(data?.summary?.unpaid_bills_count || 0).toLocaleString()} color="danger" />
                    <StatCard icon="M" title="Bills Without Invoices" value={Number(data?.missing_artifacts?.bills_without_invoices || 0).toLocaleString()} color="manufacturer" />
                </StatsGrid>

                <Section>
                    <SectionTitle>Mismatch Checks</SectionTitle>
                    <AdminDataTable columns={mismatchColumns} rows={data?.mismatches || []} loading={loading} emptyText="No mismatch checks" />
                </Section>
            </PageContent>
        </PageContainer>
    );
};

export default AdminReconciliation;
const LeftRow = styled.div`display:flex;align-items:center;gap: 0.625rem;`;
const PeriodSelect = styled(SimpleSelect)`border:1px solid ${({theme})=>theme.colors.border || '#e5e7eb'};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface || '#fff'};`;
const Section = styled.section`margin-top: 1.25rem;`;
const SectionTitle = styled.h3`margin: 0 0 0.75rem;color:${({theme})=>theme.colors.textPrimary || '#111827'};font-size:1rem;`;
const Diff = styled.span`color:${({theme,$negative})=>$negative?(theme.colors.danger||'#dc3545'):(theme.colors.success||'#28a745')};font-weight:700;`;
